<script setup lang="ts">
import { useElementHover, useFocusWithin } from "@vueuse/core";
import {
  MASCOT_SPRITE_SIZE,
  MASCOT_STAGES,
  renderMascotSprite,
} from "~/components/mascot/launcher/mascot-sprite.model";
import { useMascotLauncher } from "~/components/mascot/launcher/useMascotLauncher";

const launcherElement = useTemplateRef<HTMLButtonElement>("launcherElement");
const canvasElement = useTemplateRef<HTMLCanvasElement>("canvasElement");

const { animation, spriteState, spriteBounds, unreadCount, stage, canPreviewStages, previewStage, onClick, onHover } =
  useMascotLauncher(launcherElement);
const { t } = useI18n();

// Shown only while the pointer or the keyboard focus is on the mascot or on the buttons, so they never cover the
// page's actions.
const STAGE_PREVIEW_HIDE_DELAY_MS = 200;
const stagePreviewElement = useTemplateRef<HTMLElement>("stagePreviewElement");
const isLauncherHovered = useElementHover(launcherElement, { delayLeave: STAGE_PREVIEW_HIDE_DELAY_MS });
const isStagePreviewHovered = useElementHover(stagePreviewElement, { delayLeave: STAGE_PREVIEW_HIDE_DELAY_MS });
const { focused: isLauncherFocused } = useFocusWithin(launcherElement);
const { focused: isStagePreviewFocused } = useFocusWithin(stagePreviewElement);
const isStagePreviewShown = computed(
  () => isLauncherHovered.value || isStagePreviewHovered.value || isLauncherFocused.value || isStagePreviewFocused.value
);

// Gleap's screenshot shrinks each canvas to 2/3 of its size with smoothing: 3 canvas pixels per sprite pixel
// shrink to whole 2-pixel blocks instead of blurring the sprite.
const CANVAS_PIXELS_PER_SPRITE_PIXEL = 3;
const canvasSize = MASCOT_SPRITE_SIZE * CANVAS_PIXELS_PER_SPRITE_PIXEL;
const spriteCanvas = new OffscreenCanvas(MASCOT_SPRITE_SIZE, MASCOT_SPRITE_SIZE);

const spriteKey = computed(() => JSON.stringify(spriteState.value));
const unreadBadge = computed(() => (unreadCount.value > 9 ? "9+" : String(unreadCount.value)));
// Anchors in sprite pixels, scaled by --mascot-pixel in CSS.
const anchors = computed(() => ({
  "--mascot-badge-x": spriteBounds.value.maxX - 3,
  "--mascot-badge-y": spriteBounds.value.minY + 3,
  "--mascot-effect-x": spriteBounds.value.maxX - 6,
  "--mascot-effect-y": spriteBounds.value.minY + 2,
  "--mascot-notify-x": spriteBounds.value.minX + 2,
  "--mascot-brick-x": spriteBounds.value.minX,
  "--mascot-brick-y": spriteBounds.value.minY,
  "--mascot-brick-width": spriteBounds.value.maxX - spriteBounds.value.minX + 1,
  "--mascot-brick-height": spriteBounds.value.maxY - spriteBounds.value.minY + 1,
  "--mascot-empty-right": MASCOT_SPRITE_SIZE - 1 - spriteBounds.value.maxX,
  "--mascot-empty-bottom": MASCOT_SPRITE_SIZE - 1 - spriteBounds.value.maxY,
  "--mascot-shadow-y": spriteBounds.value.maxY - 1.5,
  "--mascot-shadow-width": Math.round((spriteBounds.value.maxX - spriteBounds.value.minX) * 0.8),
}));

watch(
  [spriteKey, canvasElement],
  () => {
    const context = canvasElement.value?.getContext("2d");
    const spriteContext = spriteCanvas.getContext("2d");
    if (!context || !spriteContext) return;
    spriteContext.putImageData(new ImageData(renderMascotSprite(spriteState.value), MASCOT_SPRITE_SIZE), 0, 0);
    context.imageSmoothingEnabled = false;
    context.clearRect(0, 0, canvasSize, canvasSize);
    context.drawImage(spriteCanvas, 0, 0, canvasSize, canvasSize);
  },
  { flush: "post" }
);

const ui = {
  launcher:
    "mascot-launcher pointer-events-none fixed z-[90] cursor-pointer border-none bg-transparent p-0 image-render-pixel focus-visible:outline focus-visible:outline-2 focus-visible:outline-red-500 focus-visible:outline-offset-[-8px] focus-visible:rounded-xl",
  canvas: "mascot-launcher__body absolute inset-0 block size-full origin-[50%_92%]",
  brick: "mascot-launcher__brick pointer-events-auto absolute",
  shadow: "mascot-launcher__shadow absolute left-1/2 -translate-x-1/2 rounded-[50%] bg-black/20",
  effect: "mascot-launcher__effect pointer-events-none absolute font-mono font-bold text-gray-900",
  badge:
    "mascot-launcher__badge pointer-events-auto absolute flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-red-500 px-1 text-xs font-bold leading-none text-white shadow-[0_0_0_2px_white,0_0_0_3px_black]",
  stagePreview: "!fixed bottom-6 right-6 z-[90]",
};
</script>

<template>
  <button
    ref="launcherElement"
    type="button"
    :class="[ui.launcher, `mascot-launcher--${animation}`]"
    :style="anchors"
    :aria-label="t('mascot.launcher.openSupport', unreadCount)"
    @click="onClick"
    @mouseenter="onHover"
  >
    <span :class="ui.shadow" aria-hidden="true"></span>
    <canvas ref="canvasElement" :width="canvasSize" :height="canvasSize" :class="ui.canvas" aria-hidden="true"></canvas>
    <span :class="ui.brick" aria-hidden="true"></span>
    <span
      v-if="animation === 'sleep' || animation === 'laugh' || animation === 'notify'"
      :class="ui.effect"
      aria-hidden="true"
    ></span>
    <span v-if="unreadCount > 0" :key="unreadCount" :class="ui.badge" aria-hidden="true">
      {{ unreadBadge }}
    </span>
  </button>
  <div
    v-if="canPreviewStages"
    ref="stagePreviewElement"
    :class="[ui.stagePreview, !isStagePreviewShown && 'invisible']"
  >
    <AwButtonGroup size="sm" role="group" :aria-label="t('mascot.launcher.stagePreview')">
      <AwButton
        v-for="previewedStage in MASCOT_STAGES"
        :key="previewedStage"
        :label="String(previewedStage)"
        variant="outline"
        :color="stage === previewedStage ? 'primary' : 'secondary'"
        :is-selected="stage === previewedStage"
        @click="previewStage(previewedStage)"
      />
    </AwButtonGroup>
  </div>
</template>

<style scoped lang="scss">
// Sprite-pixel offsets, per-animation states and keyframes: UnoCSS cannot express them.
.mascot-launcher {
  --mascot-pixel: 1.5px;
  // The brick, not the transparent canvas around it, sits where Gleap's own button was.
  right: calc(45px - var(--mascot-empty-right) * var(--mascot-pixel));
  bottom: calc(75px - var(--mascot-empty-bottom) * var(--mascot-pixel));
  width: calc(56 * var(--mascot-pixel));
  height: calc(56 * var(--mascot-pixel));
}

// Only the brick takes clicks, so the transparent canvas never covers the page's buttons.
.mascot-launcher__brick {
  left: calc(var(--mascot-brick-x) * var(--mascot-pixel));
  top: calc(var(--mascot-brick-y) * var(--mascot-pixel));
  width: calc(var(--mascot-brick-width) * var(--mascot-pixel));
  height: calc(var(--mascot-brick-height) * var(--mascot-pixel));
}

.mascot-launcher__shadow {
  top: calc(var(--mascot-shadow-y) * var(--mascot-pixel));
  width: calc(var(--mascot-shadow-width) * var(--mascot-pixel));
  height: calc(3 * var(--mascot-pixel));
}

.mascot-launcher__badge {
  left: calc(var(--mascot-badge-x) * var(--mascot-pixel));
  top: calc(var(--mascot-badge-y) * var(--mascot-pixel));
  transform: translate(-50%, -50%);
  animation: mascot-badge-pop 0.6s steps(6, end);
}

.mascot-launcher__effect {
  left: calc(var(--mascot-effect-x) * var(--mascot-pixel));
  top: calc(var(--mascot-effect-y) * var(--mascot-pixel));
}

// With its button hidden, Gleap anchors its widget and notifications where the button was, over the
// launcher: lift them above it. Below 451px Gleap opens full screen, so it is left alone.
@media (min-width: 451px) {
  :global(.gleap-frame-container.gleap-frame-container--no-button),
  :global(.gleap-notification-container.gleap-notification-container--no-button) {
    bottom: 165px;
  }

  // Gleap sizes the widget from its own bottom offset (100vh - 84px - offset).
  :global(.gleap-frame-container--no-button .gleap-frame-container-inner) {
    height: calc(100vh - 249px);
  }
}

.mascot-launcher--idle .mascot-launcher__body {
  animation: mascot-breathe 1.8s steps(1, end) infinite;
}

.mascot-launcher--sleep {
  .mascot-launcher__body {
    animation: mascot-breathe 3.2s steps(1, end) infinite;
  }

  .mascot-launcher__effect::after {
    content: "z";
    display: block;
    font-size: 12px;
    animation: mascot-snore 2.6s linear infinite;
  }
}

.mascot-launcher--greet .mascot-launcher__body {
  animation: mascot-hop 0.45s ease-out 1;
}

.mascot-launcher--notify {
  .mascot-launcher__body {
    animation: mascot-jump 0.7s ease-in-out 2;
  }

  .mascot-launcher__shadow {
    animation: mascot-shadow-jump 0.7s ease-in-out 2;
  }

  // On the left: the unread badge covers the top right corner.
  .mascot-launcher__effect {
    left: calc(var(--mascot-notify-x) * var(--mascot-pixel));
  }

  .mascot-launcher__effect::after {
    content: "!";
    display: block;
    font-size: 16px;
    color: #ffd447;
    text-shadow: 2px 2px 0 #141414;
    transform: translate(-4px, -20px);
    animation: mascot-blink 1.4s steps(1, end) forwards;
  }
}

.mascot-launcher--laugh {
  .mascot-launcher__body {
    animation: mascot-shake 0.22s steps(1, end) infinite;
  }

  .mascot-launcher__effect::after {
    content: "HA!";
    display: block;
    font-size: 10px;
    animation: mascot-float 1s steps(5, end) forwards;
  }
}

.mascot-launcher--evolve .mascot-launcher__body {
  animation: mascot-pulse 0.3s steps(1, end) infinite;
}

@media (prefers-reduced-motion: reduce) {
  .mascot-launcher *,
  .mascot-launcher *::after {
    animation: none !important;
  }
}

@keyframes mascot-breathe {
  50% {
    transform: translateY(calc(-1 * var(--mascot-pixel)));
  }
}

@keyframes mascot-hop {
  50% {
    transform: translateY(calc(-3 * var(--mascot-pixel))) rotate(-4deg);
  }
}

@keyframes mascot-jump {
  15% {
    transform: scale(1.1, 0.86);
  }
  45% {
    transform: translateY(calc(-8 * var(--mascot-pixel))) scale(0.92, 1.1);
  }
  65% {
    transform: translateY(calc(-8 * var(--mascot-pixel)));
  }
  85% {
    transform: scale(1.08, 0.9);
  }
}

@keyframes mascot-shadow-jump {
  45%,
  65% {
    transform: translateX(-50%) scale(0.6);
    opacity: 0.5;
  }
}

@keyframes mascot-shake {
  25% {
    transform: translateX(var(--mascot-pixel)) rotate(3deg);
  }
  50% {
    transform: translateY(calc(-1 * var(--mascot-pixel)));
  }
  75% {
    transform: translateX(calc(-1 * var(--mascot-pixel))) rotate(-3deg);
  }
}

@keyframes mascot-pulse {
  50% {
    transform: scale(1.04);
  }
}

@keyframes mascot-snore {
  0% {
    transform: translate(0, 0);
    opacity: 0;
  }
  15% {
    opacity: 1;
  }
  100% {
    transform: translate(16px, -28px) scale(1.6);
    opacity: 0;
  }
}

@keyframes mascot-float {
  to {
    transform: translate(8px, -20px);
    opacity: 0;
  }
}

@keyframes mascot-blink {
  0%,
  40%,
  80% {
    opacity: 1;
  }
  20%,
  60% {
    opacity: 0.2;
  }
  100% {
    opacity: 0;
  }
}

@keyframes mascot-badge-pop {
  30% {
    transform: translate(-50%, -50%) scale(1.45);
  }
  60% {
    transform: translate(-50%, -50%) scale(0.9);
  }
}
</style>
